import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area, ComposedChart, ReferenceLine
} from 'recharts';
import { 
  TrendingUp, TrendingDown, Users, ShoppingCart, DollarSign, 
  Package, Calendar, X, ChevronRight, Filter, Download, Activity,
  BarChart2, Target, FileSpreadsheet
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Colors for charts
const COLORS = ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16'];

// Format currency
const formatCurrency = (value) => {
  return new Intl.NumberFormat('ru-RU', { style: 'decimal' }).format(value) + ' ₸';
};

// KPI Card Component — `change` can be null/undefined to hide the comparison line
const KPICard = ({ title, value, change, icon: Icon, color, subtitle, testId }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow" data-testid={testId}>
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-gray-500 mb-1">{title}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        {change !== null && change !== undefined && (
          <div className={`flex items-center mt-2 text-sm ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {change >= 0 ? <TrendingUp className="w-4 h-4 mr-1" /> : <TrendingDown className="w-4 h-4 mr-1" />}
            {Math.abs(change)}% vs прошлый период
          </div>
        )}
        {subtitle && (
          <p className="text-xs text-gray-400 mt-1">{subtitle}</p>
        )}
      </div>
      <div className={`w-12 h-12 rounded-xl ${color} flex items-center justify-center`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
    </div>
  </div>
);

// Client Detail Modal
const ClientDetailModal = ({ clientId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/analytics/powerbi/client/${clientId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setData(response.data);
      } catch (error) {
        console.error('Error fetching client details:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [clientId]);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl p-8">
          <div className="animate-spin w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full"></div>
        </div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 rounded-t-2xl">
          <button onClick={onClose} className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-lg">
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center text-2xl font-bold">
              {data.client.role === 'B2B' ? '🏢' : '👤'}
            </div>
            <div>
              <h2 className="text-2xl font-bold">{data.client.company_name || data.client.full_name || data.client.email}</h2>
              <p className="text-white/80">{data.client.role} • {data.client.email}</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Metrics */}
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-indigo-50 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-indigo-600">{formatCurrency(data.metrics.total_revenue)}</p>
              <p className="text-sm text-gray-600">Общая выручка</p>
            </div>
            <div className="bg-green-50 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-green-600">{data.metrics.total_orders}</p>
              <p className="text-sm text-gray-600">Заказов</p>
            </div>
            <div className="bg-amber-50 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-amber-600">{formatCurrency(data.metrics.avg_order_value)}</p>
              <p className="text-sm text-gray-600">Средний чек</p>
            </div>
            <div className="bg-purple-50 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-purple-600">{data.metrics.lifetime_days}</p>
              <p className="text-sm text-gray-600">Дней с нами</p>
            </div>
          </div>

          {/* Monthly Trend Chart */}
          <div className="bg-gray-50 rounded-xl p-4">
            <h3 className="font-semibold text-gray-900 mb-4">📈 Динамика покупок</h3>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={data.monthly_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Area type="monotone" dataKey="revenue" stroke="#6366f1" fill="#6366f1" fillOpacity={0.2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="grid grid-cols-2 gap-6">
            {/* Network Breakdown (for B2B companies) */}
            {data.network_breakdown && data.network_breakdown.length > 0 && (
              <div className="col-span-2 bg-gray-50 rounded-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-4">🏪 Разбивка по точкам / сетям</h3>
                <div className="space-y-3">
                  {data.network_breakdown.map((net, index) => {
                    const pct = data.metrics.total_revenue > 0 ? (net.revenue / data.metrics.total_revenue * 100) : 0;
                    return (
                    <div key={net.id} className="bg-white p-3 rounded-lg">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-3">
                          <span className={`w-7 h-7 rounded-lg flex items-center justify-center text-white text-xs font-bold ${
                            index === 0 ? 'bg-indigo-500' : index === 1 ? 'bg-indigo-400' : 'bg-indigo-300'
                          }`}>{index + 1}</span>
                          <div>
                            <p className="font-medium text-sm text-gray-900">{net.name}</p>
                            <p className="text-xs text-gray-500">{net.orders} заказов • {net.items} шт</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-sm">{formatCurrency(net.revenue)}</p>
                          <p className="text-xs text-gray-500">{pct.toFixed(1)}%</p>
                        </div>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
                        <div className="bg-indigo-500 h-1.5 rounded-full" style={{width: `${Math.min(pct, 100)}%`}}></div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Favorite Products */}
            <div className="bg-gray-50 rounded-xl p-4">
              <h3 className="font-semibold text-gray-900 mb-4">🛒 Любимые товары</h3>
              <div className="space-y-3">
                {data.favorite_products.map((product, index) => (
                  <div key={product.id} className="flex items-center justify-between bg-white p-3 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center text-xs font-bold">
                        {index + 1}
                      </span>
                      <span className="text-sm">{product.name}</span>
                    </div>
                    <span className="text-sm font-semibold">{product.quantity} шт</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Orders */}
            <div className="bg-gray-50 rounded-xl p-4">
              <h3 className="font-semibold text-gray-900 mb-4">📦 Последние заказы</h3>
              <div className="space-y-2">
                {data.recent_orders.slice(0, 5).map(order => (
                  <div key={order.id} className="flex items-center justify-between bg-white p-3 rounded-lg">
                    <div>
                      <p className="text-sm font-medium">{order.order_number}</p>
                      <p className="text-xs text-gray-500">
                        {new Date(order.created_at).toLocaleDateString('ru-RU')}
                        {order.network_name && <span className="ml-1 text-indigo-500">• {order.network_name}</span>}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold">{formatCurrency(order.total_amount)}</p>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        order.status === 'Delivered' ? 'bg-green-100 text-green-700' :
                        order.status === 'Cancelled' ? 'bg-red-100 text-red-700' :
                        'bg-amber-100 text-amber-700'
                      }`}>{order.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Product Detail Modal
const ProductDetailModal = ({ productId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/analytics/powerbi/product/${productId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setData(response.data);
      } catch (error) {
        console.error('Error fetching product details:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [productId]);

  if (loading || !data) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl p-8">
          <div className="animate-spin w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-gradient-to-r from-green-600 to-emerald-600 text-white p-6 rounded-t-2xl">
          <button onClick={onClose} className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-lg">
            <X className="w-5 h-5" />
          </button>
          <h2 className="text-2xl font-bold">{data.product.name}</h2>
          <p className="text-white/80">Розничная цена: {formatCurrency(data.product.retail_price)}</p>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-green-50 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-green-600">{formatCurrency(data.metrics.total_revenue)}</p>
              <p className="text-sm text-gray-600">Выручка</p>
            </div>
            <div className="bg-blue-50 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-blue-600">{data.metrics.total_quantity}</p>
              <p className="text-sm text-gray-600">Продано штук</p>
            </div>
            <div className="bg-purple-50 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-purple-600">{data.metrics.total_orders}</p>
              <p className="text-sm text-gray-600">Заказов</p>
            </div>
          </div>

          <div className="bg-gray-50 rounded-xl p-4">
            <h3 className="font-semibold text-gray-900 mb-4">📈 Продажи по месяцам</h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={data.monthly_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="quantity" fill="#22c55e" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-gray-50 rounded-xl p-4">
            <h3 className="font-semibold text-gray-900 mb-4">👥 Топ покупатели</h3>
            <div className="space-y-2">
              {data.top_buyers.map((buyer, index) => (
                <div key={buyer.id} className="flex items-center justify-between bg-white p-3 rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-xs font-bold">
                      {index + 1}
                    </span>
                    <span className="text-sm">{buyer.name || 'Клиент'}</span>
                  </div>
                  <span className="text-sm font-semibold">{buyer.quantity} шт</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Dashboard Component
export default function AnalyticsPowerBI() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [revenueTrend, setRevenueTrend] = useState([]);
  const [categoryBreakdown, setCategoryBreakdown] = useState([]);
  const [topClients, setTopClients] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [cityComparison, setCityComparison] = useState([]);
  
  // New states for advanced analytics
  const [cohortData, setCohortData] = useState(null);
  const [forecastData, setForecastData] = useState(null);
  const [abcData, setAbcData] = useState(null);
  const [periodComparison, setPeriodComparison] = useState(null);
  const [mlForecast, setMlForecast] = useState(null);
  const [productForecast, setProductForecast] = useState(null);
  const [filteredData, setFilteredData] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  
  // Filters
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [period, setPeriod] = useState('day');
  
  // Cross-filtering
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedClientType, setSelectedClientType] = useState(null);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [selectedClientName, setSelectedClientName] = useState('');
  const [selectedProductName, setSelectedProductName] = useState('');
  const [selectedAbcCategory, setSelectedAbcCategory] = useState(null);
  const [filterOptions, setFilterOptions] = useState({ cities: [], categories: [] });
  
  // Modals
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  
  // Tabs for ABC analysis
  const [abcTab, setAbcTab] = useState('products');
  
  // ML Forecast tab
  const [mlTab, setMlTab] = useState('revenue');

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateRange.start) params.append('start_date', dateRange.start);
      if (dateRange.end) params.append('end_date', dateRange.end);
      if (selectedCity) params.append('city_id', selectedCity);
      if (selectedCategory) params.append('category_id', selectedCategory);
      if (selectedClientType) params.append('client_type', selectedClientType);
      if (selectedClientId) params.append('client_id', selectedClientId);
      if (selectedProductId) params.append('product_id', selectedProductId);

      const results = await Promise.allSettled([
        axios.get(`${API}/analytics/powerbi/overview?${params}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/analytics/powerbi/revenue-trend?period=${period}&${params}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/analytics/powerbi/category-breakdown?${params}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/analytics/powerbi/top-clients?${params}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/analytics/powerbi/top-products?${params}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/analytics/powerbi/city-comparison?${params}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/analytics/powerbi/cohort-analysis`, { headers: getAuthHeaders() }),
        axios.get(`${API}/analytics/powerbi/forecasts`, { headers: getAuthHeaders() }),
        axios.get(`${API}/analytics/powerbi/abc-analysis?${params}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/analytics/powerbi/ml-forecast?forecast_months=3`, { headers: getAuthHeaders() }),
        axios.get(`${API}/analytics/powerbi/ml-product-forecast?top_n=10&forecast_months=3`, { headers: getAuthHeaders() }),
        axios.get(`${API}/analytics/powerbi/filtered-data?${params}`, { headers: getAuthHeaders() })
      ]);

      if (results[0].status === 'fulfilled') setOverview(results[0].value.data);
      if (results[1].status === 'fulfilled') setRevenueTrend(results[1].value.data);
      if (results[2].status === 'fulfilled') setCategoryBreakdown(results[2].value.data);
      if (results[3].status === 'fulfilled') setTopClients(results[3].value.data);
      if (results[4].status === 'fulfilled') setTopProducts(results[4].value.data);
      if (results[5].status === 'fulfilled') setCityComparison(results[5].value.data);
      if (results[6].status === 'fulfilled') setCohortData(results[6].value.data);
      if (results[7].status === 'fulfilled') setForecastData(results[7].value.data);
      if (results[8].status === 'fulfilled') setAbcData(results[8].value.data);
      if (results[9].status === 'fulfilled') setMlForecast(results[9].value.data);
      if (results[10].status === 'fulfilled') setProductForecast(results[10].value.data);
      if (results[11].status === 'fulfilled') {
        setFilteredData(results[11].value.data);
        setFilterOptions(results[11].value.data.filter_options || { cities: [], categories: [] });
      }
      
      // Fetch period comparison if dates are set
      if (dateRange.start && dateRange.end) {
        try {
          const compRes = await axios.get(
            `${API}/analytics/powerbi/period-comparison?current_start=${dateRange.start}&current_end=${dateRange.end}`,
            { headers: getAuthHeaders() }
          );
          setPeriodComparison(compRes.data);
        } catch (e) {
          setPeriodComparison(null);
        }
      } else {
        setPeriodComparison(null);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const clearFilters = () => {
    setSelectedCity(null);
    setSelectedCategory(null);
    setSelectedClientType(null);
    setSelectedClientId(null);
    setSelectedProductId(null);
    setSelectedClientName('');
    setSelectedProductName('');
    setDateRange({ start: '', end: '' });
  };
  
  const hasActiveFilters = selectedCity || selectedCategory || selectedClientType || selectedClientId || selectedProductId || dateRange.start || dateRange.end;

  const handleExportExcel = async () => {
    setExportLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateRange.start) params.append('start_date', dateRange.start);
      if (dateRange.end) params.append('end_date', dateRange.end);
      
      const response = await axios.get(`${API}/analytics/powerbi/export-excel?${params}`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `analytics_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export error:', error);
      alert('Ошибка экспорта');
    } finally {
      setExportLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [dateRange, period, selectedCity, selectedCategory, selectedClientType, selectedClientId, selectedProductId]);

  // Quick date range presets
  const setQuickRange = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    setDateRange({
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    });
  };

  if (loading && !overview) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Загрузка аналитики...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6" data-testid="analytics-powerbi">
      {/* Header */}
      <div className="mb-8">
        <button 
          data-testid="analytics-back-btn"
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 mb-3 transition-colors"
        >
          <ChevronRight className="w-4 h-4 rotate-180" />
          Назад
        </button>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">📊 Аналитика</h1>
        <p className="text-gray-600">Интерактивный дашборд в стиле Power BI</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-400" />
            <span className="text-sm font-medium text-gray-700">Фильтры:</span>
          </div>
          
          {/* Quick ranges */}
          <div className="flex gap-2">
            <button onClick={() => setQuickRange(7)} className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
              7 дней
            </button>
            <button onClick={() => setQuickRange(30)} className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
              30 дней
            </button>
            <button onClick={() => setQuickRange(90)} className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
              90 дней
            </button>
            <button onClick={() => setDateRange({ start: '', end: '' })} className="px-3 py-1.5 text-sm bg-indigo-100 text-indigo-700 hover:bg-indigo-200 rounded-lg transition-colors">
              Всё время
            </button>
          </div>

          {/* Date inputs */}
          <div className="flex items-center gap-2 ml-auto">
            <input
              type="date"
              value={dateRange.start}
              onChange={e => setDateRange(prev => ({ ...prev, start: e.target.value }))}
              className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
            />
            <span className="text-gray-400">—</span>
            <input
              type="date"
              value={dateRange.end}
              onChange={e => setDateRange(prev => ({ ...prev, end: e.target.value }))}
              className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
            />
            <button
              onClick={handleExportExcel}
              disabled={exportLoading}
              className="ml-4 px-4 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              <FileSpreadsheet className="w-4 h-4" />
              {exportLoading ? 'Экспорт...' : 'Excel'}
            </button>
          </div>
        </div>
        
        {/* Cross-filter dropdowns */}
        <div className="flex flex-wrap items-center gap-4 mt-4 pt-4 border-t border-gray-100">
          <span className="text-sm text-gray-500">Кросс-фильтры:</span>
          
          {/* City filter */}
          <select
            value={selectedCity || ''}
            onChange={(e) => setSelectedCity(e.target.value || null)}
            className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm bg-white"
          >
            <option value="">Все города</option>
            {filterOptions.cities.map(city => (
              <option key={city.id} value={city.id}>{city.name}</option>
            ))}
          </select>
          
          {/* Category filter */}
          <select
            value={selectedCategory || ''}
            onChange={(e) => setSelectedCategory(e.target.value || null)}
            className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm bg-white"
          >
            <option value="">Все категории</option>
            {filterOptions.categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
          
          {/* Client type filter */}
          <select
            value={selectedClientType || ''}
            onChange={(e) => setSelectedClientType(e.target.value || null)}
            className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm bg-white"
          >
            <option value="">Все клиенты</option>
            <option value="B2B">B2B</option>
            <option value="B2C">B2C</option>
          </select>
          
          {/* Clear filters button */}
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="px-3 py-1.5 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors flex items-center gap-1"
            >
              <X className="w-4 h-4" />
              Сбросить фильтры
            </button>
          )}
        </div>
        
        {/* Active filters badges */}
        {hasActiveFilters && (
          <div className="flex flex-wrap gap-2 mt-3">
            {selectedCity && filterOptions.cities.find(c => c.id === selectedCity) && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                🏙️ {filterOptions.cities.find(c => c.id === selectedCity)?.name}
                <button onClick={() => setSelectedCity(null)} className="ml-1 hover:text-blue-900">×</button>
              </span>
            )}
            {selectedCategory && filterOptions.categories.find(c => c.id === selectedCategory) && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                📦 {filterOptions.categories.find(c => c.id === selectedCategory)?.name}
                <button onClick={() => setSelectedCategory(null)} className="ml-1 hover:text-purple-900">×</button>
              </span>
            )}
            {selectedClientType && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                👥 {selectedClientType}
                <button onClick={() => setSelectedClientType(null)} className="ml-1 hover:text-green-900">×</button>
              </span>
            )}
            {selectedClientId && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                👤 {selectedClientName || 'Клиент'}
                <button onClick={() => { setSelectedClientId(null); setSelectedClientName(''); }} className="ml-1 hover:text-emerald-900">×</button>
              </span>
            )}
            {selectedProductId && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                🛒 {selectedProductName || 'Продукт'}
                <button onClick={() => { setSelectedProductId(null); setSelectedProductName(''); }} className="ml-1 hover:text-amber-900">×</button>
              </span>
            )}
            {dateRange.start && dateRange.end && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                📅 {dateRange.start} — {dateRange.end}
              </span>
            )}
          </div>
        )}
      </div>

      {/* KPI Cards */}
      {overview && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8" data-testid="kpi-grid">
          <KPICard
            testId="kpi-revenue"
            title="Общая выручка"
            value={formatCurrency(overview.total_revenue)}
            change={overview.revenue_change_percent}
            icon={DollarSign}
            color="bg-indigo-500"
            subtitle="Без отменённых заказов"
          />
          <KPICard
            testId="kpi-orders"
            title="Заказов"
            value={overview.total_orders}
            change={overview.orders_change_percent}
            icon={ShoppingCart}
            color="bg-green-500"
            subtitle={overview.orders_by_status ? Object.entries(overview.orders_by_status).filter(([k]) => k !== 'Cancelled').map(([k, v]) => `${k === 'Confirmed' ? 'подтв.' : k === 'Delivered' ? 'дост.' : k}: ${v}`).join(' • ') : null}
          />
          <KPICard
            testId="kpi-aov"
            title="Средний чек"
            value={formatCurrency(overview.avg_order_value)}
            change={overview.avg_order_change_percent}
            icon={TrendingUp}
            color="bg-amber-500"
          />
          <KPICard
            testId="kpi-customers"
            title="Уникальных клиентов"
            value={overview.unique_customers}
            change={overview.customers_change_percent}
            icon={Users}
            color="bg-purple-500"
          />
          <KPICard
            testId="kpi-cancelled"
            title="Отмены"
            value={`${overview.cancelled_orders || 0} • ${overview.cancellation_rate || 0}%`}
            icon={X}
            color="bg-red-500"
            subtitle={overview.cancelled_revenue > 0 ? `Потеряно: ${formatCurrency(overview.cancelled_revenue)}` : 'Нет отменённых заказов'}
          />
        </div>
      )}

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Revenue Trend */}
        <div className={`lg:col-span-2 bg-white rounded-xl shadow-sm border p-6 transition-all ${hasActiveFilters ? 'border-indigo-200' : 'border-gray-100'}`}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">📈 Динамика выручки</h2>
            <div className="flex gap-2">
              {['day', 'week', 'month'].map(p => (
                <button
                  key={p}
                  onClick={() => setPeriod(p)}
                  className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                    period === p ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {p === 'day' ? 'День' : p === 'week' ? 'Неделя' : 'Месяц'}
                </button>
              ))}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={revenueTrend}>
              <defs>
                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
              <Tooltip formatter={(value) => formatCurrency(value)} />
              <Area type="monotone" dataKey="revenue" stroke="#6366f1" fill="url(#colorRevenue)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Category Breakdown - INTERACTIVE */}
        <div className={`bg-white rounded-xl shadow-sm border p-6 transition-all ${selectedCategory ? 'border-purple-300 ring-2 ring-purple-100' : 'border-gray-100'}`}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">🥧 По категориям</h2>
            {selectedCategory && (
              <button onClick={() => setSelectedCategory(null)} className="text-xs text-purple-600 hover:text-purple-800 flex items-center gap-1">
                <X className="w-3 h-3" /> Сбросить
              </button>
            )}
          </div>
          <p className="text-xs text-gray-400 mb-2">Кликните для фильтрации</p>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={categoryBreakdown}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={2}
                dataKey="revenue"
                label={({ name, percentage }) => `${percentage}%`}
                style={{ cursor: 'pointer' }}
                onClick={(data) => {
                  if (!data || !data.name) return;
                  const cat = filterOptions.categories.find(c => c.name === data.name);
                  if (cat) {
                    setSelectedCategory(prev => prev === cat.id ? null : cat.id);
                  }
                }}
              >
                {categoryBreakdown.map((entry, index) => {
                  const cat = filterOptions.categories.find(c => c.name === entry.name);
                  const isSelected = selectedCategory && cat && selectedCategory === cat.id;
                  const isDimmed = selectedCategory && !isSelected;
                  return (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={COLORS[index % COLORS.length]} 
                      opacity={isDimmed ? 0.3 : 1}
                      stroke={isSelected ? '#7c3aed' : 'none'}
                      strokeWidth={isSelected ? 3 : 0}
                    />
                  );
                })}
              </Pie>
              <Tooltip formatter={(value) => formatCurrency(value)} />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-4 space-y-2">
            {categoryBreakdown.slice(0, 4).map((cat, index) => {
              const catOption = filterOptions.categories.find(c => c.name === cat.name);
              const isSelected = selectedCategory && catOption && selectedCategory === catOption.id;
              const isDimmed = selectedCategory && !isSelected;
              return (
                <div 
                  key={cat.name} 
                  onClick={() => {
                    if (catOption) setSelectedCategory(prev => prev === catOption.id ? null : catOption.id);
                  }}
                  className={`flex items-center justify-between text-sm cursor-pointer rounded-lg px-2 py-1 transition-all ${
                    isSelected ? 'bg-purple-50 ring-1 ring-purple-200' : isDimmed ? 'opacity-40' : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index] }}></div>
                    <span className="text-gray-600">{cat.name}</span>
                  </div>
                  <span className="font-medium">{cat.percentage}%</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Top Clients - Clickable */}
        <div className={`bg-white rounded-xl shadow-sm border p-6 transition-all ${selectedClientId ? 'border-emerald-300 ring-2 ring-emerald-100' : 'border-gray-100'}`}>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-semibold text-gray-900">👥 Топ клиенты</h2>
            {selectedClientId && (
              <button onClick={() => { setSelectedClientId(null); setSelectedClientName(''); }} className="text-xs text-emerald-600 hover:text-emerald-800 flex items-center gap-1">
                <X className="w-3 h-3" /> Сбросить
              </button>
            )}
          </div>
          <p className="text-xs text-gray-400 mb-3">Кликните для фильтрации • стрелка для деталей</p>
          <div className="space-y-3">
            {topClients.slice(0, 6).map((client, index) => {
              const isSelected = selectedClientId === client.id;
              const isDimmed = selectedClientId && !isSelected;
              return (
              <div
                key={client.id}
                className={`flex items-center justify-between p-4 rounded-xl cursor-pointer transition-all ${
                  isSelected ? 'bg-emerald-50 ring-1 ring-emerald-200' : isDimmed ? 'bg-gray-50 opacity-40' : 'bg-gray-50 hover:bg-indigo-50'
                }`}
              >
                <div 
                  className="flex items-center gap-4 flex-1"
                  onClick={() => {
                    if (isSelected) { setSelectedClientId(null); setSelectedClientName(''); }
                    else { setSelectedClientId(client.id); setSelectedClientName(client.name || client.email); }
                  }}
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold ${
                    index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : index === 2 ? 'bg-amber-600' : 'bg-gray-300'
                  }`}>
                    {index + 1}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{client.name || client.email}</p>
                    <p className="text-sm text-gray-500">
                      {client.role} • {client.orders} заказов
                      {client.is_company && client.network_count > 0 && (
                        <span className="ml-1 text-indigo-500">{` • ${client.network_count} ${client.network_count === 1 ? 'точка' : client.network_count < 5 ? 'точки' : 'точек'}`}</span>
                      )}
                    </p>
                    {client.networks && client.networks.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {client.networks.slice(0, 3).map(net => (
                          <span key={net.id} className="text-xs bg-indigo-50 text-indigo-600 px-1.5 py-0.5 rounded">{net.name}</span>
                        ))}
                        {client.networks.length > 3 && <span className="text-xs text-gray-400">+{client.networks.length - 3}</span>}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className="font-semibold text-gray-900">{formatCurrency(client.revenue)}</p>
                    <p className="text-sm text-gray-500">Ср. чек: {formatCurrency(client.avg_order)}</p>
                  </div>
                  <button 
                    onClick={(e) => { e.stopPropagation(); setSelectedClient(client.id); }}
                    className="p-1 hover:bg-indigo-100 rounded-lg transition-colors"
                    title="Подробнее"
                  >
                    <ChevronRight className="w-5 h-5 text-gray-400 hover:text-indigo-500" />
                  </button>
                </div>
              </div>
              );
            })}
          </div>
        </div>

        {/* Top Products - Clickable */}
        <div className={`bg-white rounded-xl shadow-sm border p-6 transition-all ${selectedProductId ? 'border-amber-300 ring-2 ring-amber-100' : 'border-gray-100'}`}>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-semibold text-gray-900">🏆 Топ продукты</h2>
            {selectedProductId && (
              <button onClick={() => { setSelectedProductId(null); setSelectedProductName(''); }} className="text-xs text-amber-600 hover:text-amber-800 flex items-center gap-1">
                <X className="w-3 h-3" /> Сбросить
              </button>
            )}
          </div>
          <p className="text-xs text-gray-400 mb-3">Кликните для фильтрации • стрелка для деталей</p>
          <div className="space-y-3">
            {topProducts.slice(0, 6).map((product, index) => {
              const isSelected = selectedProductId === product.id;
              const isDimmed = selectedProductId && !isSelected;
              return (
              <div
                key={product.id}
                className={`flex items-center justify-between p-4 rounded-xl cursor-pointer transition-all ${
                  isSelected ? 'bg-amber-50 ring-1 ring-amber-200' : isDimmed ? 'bg-gray-50 opacity-40' : 'bg-gray-50 hover:bg-green-50'
                }`}
              >
                <div 
                  className="flex items-center gap-4 flex-1"
                  onClick={() => {
                    if (isSelected) { setSelectedProductId(null); setSelectedProductName(''); }
                    else { setSelectedProductId(product.id); setSelectedProductName(product.name); }
                  }}
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold ${
                    index === 0 ? 'bg-green-500' : index === 1 ? 'bg-green-400' : index === 2 ? 'bg-green-300' : 'bg-gray-300'
                  }`}>
                    {index + 1}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 line-clamp-1">{product.name}</p>
                    <p className="text-sm text-gray-500">{product.quantity} шт продано</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <p className="font-semibold text-gray-900">{formatCurrency(product.revenue)}</p>
                  <button 
                    onClick={(e) => { e.stopPropagation(); setSelectedProduct(product.id); }}
                    className="p-1 hover:bg-green-100 rounded-lg transition-colors"
                    title="Подробнее"
                  >
                    <ChevronRight className="w-5 h-5 text-gray-400 hover:text-green-500" />
                  </button>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* City Comparison - INTERACTIVE */}
      <div className={`bg-white rounded-xl shadow-sm border p-6 mb-6 transition-all ${selectedCity ? 'border-blue-300 ring-2 ring-blue-100' : 'border-gray-100'}`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">🏙️ Сравнение по городам</h2>
          <div className="flex items-center gap-2">
            <p className="text-xs text-gray-400">Кликните для фильтрации</p>
            {selectedCity && (
              <button onClick={() => setSelectedCity(null)} className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1">
                <X className="w-3 h-3" /> Сбросить
              </button>
            )}
          </div>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart 
            data={cityComparison} 
            layout="vertical"
            onClick={(data) => {
              if (data && data.activePayload && data.activePayload[0]) {
                const cityName = data.activePayload[0].payload?.city;
                const city = filterOptions.cities.find(c => c.name === cityName);
                if (city) {
                  setSelectedCity(prev => prev === city.id ? null : city.id);
                }
              }
            }}
            style={{ cursor: 'pointer' }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis type="number" tick={{ fontSize: 12 }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
            <YAxis type="category" dataKey="city" tick={{ fontSize: 12 }} width={100} />
            <Tooltip formatter={(value) => formatCurrency(value)} />
            <Legend />
            <Bar dataKey="revenue" fill="#6366f1" name="Выручка" radius={[0, 4, 4, 0]}>
              {cityComparison.map((entry, index) => {
                const city = filterOptions.cities.find(c => c.name === entry.city);
                const isSelected = selectedCity && city && selectedCity === city.id;
                const isDimmed = selectedCity && !isSelected;
                return <Cell key={index} fill={isSelected ? '#4f46e5' : '#6366f1'} opacity={isDimmed ? 0.3 : 1} />;
              })}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Sales Forecast Section */}
      {forecastData && forecastData.historical && forecastData.historical.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">🔮 Прогноз продаж</h2>
              <p className="text-sm text-gray-500">
                Тренд: {forecastData.metrics?.trend === 'growing' ? '📈 Растущий' : 
                        forecastData.metrics?.trend === 'declining' ? '📉 Снижающийся' : '➡️ Стабильный'}
                {forecastData.metrics?.monthly_revenue_change ? ` (${forecastData.metrics.monthly_revenue_change > 0 ? '+' : ''}${formatCurrency(forecastData.metrics.monthly_revenue_change)}/мес)` : ''}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Target className="w-5 h-5 text-indigo-500" />
              <span className="text-sm text-gray-600">Прогноз на 3 месяца</span>
            </div>
          </div>
          
          {/* Data-quality warnings */}
          {forecastData.warnings && forecastData.warnings.length > 0 && (
            <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="forecast-warnings">
              <p className="text-xs font-medium text-amber-800 mb-1">⚠️ Предупреждение о качестве прогноза:</p>
              <ul className="text-xs text-amber-700 space-y-0.5 list-disc list-inside">
                {forecastData.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Revenue Forecast Chart */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">Прогноз выручки</h3>
              <ResponsiveContainer width="100%" height={250}>
                <ComposedChart data={[...forecastData.historical, ...forecastData.forecast]}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend />
                  {/* Historical data */}
                  <Bar dataKey="revenue" fill="#6366f1" name="Факт" radius={[4, 4, 0, 0]} />
                  {/* Trend line */}
                  <Line type="monotone" dataKey="revenue_trend" stroke="#f59e0b" strokeWidth={2} strokeDasharray="5 5" name="Тренд" dot={false} />
                  {/* Forecast */}
                  <Bar dataKey="revenue_forecast" fill="#22c55e" name="Прогноз" radius={[4, 4, 0, 0]} opacity={0.7} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            
            {/* Orders Forecast Chart */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">Прогноз заказов</h3>
              <ResponsiveContainer width="100%" height={250}>
                <ComposedChart data={[...forecastData.historical, ...forecastData.forecast]}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="orders" fill="#8b5cf6" name="Факт" radius={[4, 4, 0, 0]} />
                  <Line type="monotone" dataKey="orders_trend" stroke="#f59e0b" strokeWidth={2} strokeDasharray="5 5" name="Тренд" dot={false} />
                  <Bar dataKey="orders_forecast" fill="#06b6d4" name="Прогноз" radius={[4, 4, 0, 0]} opacity={0.7} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
          
          {/* Seasonality */}
          {forecastData.seasonality && forecastData.seasonality.length > 0 && (
            <div className="mt-6 pt-6 border-t border-gray-100">
              <h3 className="text-sm font-medium text-gray-700 mb-3">📅 Сезонность по дням недели</h3>
              <ResponsiveContainer width="100%" height={150}>
                <BarChart data={forecastData.seasonality}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                  <Tooltip formatter={(value, name) => [name === 'avg_revenue' ? formatCurrency(value) : value, name === 'avg_revenue' ? 'Ср. выручка' : 'Ср. заказов']} />
                  <Bar dataKey="avg_revenue" fill="#6366f1" name="Ср. выручка" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {/* Cohort Analysis Section */}
      {cohortData && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">👥 Когортный анализ</h2>
              <p className="text-sm text-gray-500">Удержание клиентов по месяцу первой покупки</p>
            </div>
            <Activity className="w-5 h-5 text-indigo-500" />
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Cohort Retention Table */}
            <div className="lg:col-span-2 overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-3 py-2 text-left font-medium text-gray-600">Когорта</th>
                    <th className="px-3 py-2 text-center font-medium text-gray-600">Размер</th>
                    {[0, 1, 2, 3, 4, 5].map(m => (
                      <th key={m} className="px-3 py-2 text-center font-medium text-gray-600">
                        {m === 0 ? 'М0' : `+${m}м`}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {cohortData.cohorts?.slice(-6).map((cohort, idx) => (
                    <tr key={cohort.cohort} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-3 py-2 font-medium text-gray-900">{cohort.cohort}</td>
                      <td className="px-3 py-2 text-center text-gray-600">{cohort.size}</td>
                      {cohort.retention?.slice(0, 6).map((ret, i) => (
                        <td key={i} className="px-3 py-2 text-center">
                          <span 
                            className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                              ret.percentage >= 80 ? 'bg-green-100 text-green-700' :
                              ret.percentage >= 50 ? 'bg-yellow-100 text-yellow-700' :
                              ret.percentage >= 20 ? 'bg-orange-100 text-orange-700' :
                              'bg-red-100 text-red-700'
                            }`}
                          >
                            {ret.percentage}%
                          </span>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-xs text-gray-500 mt-2">* М0 = месяц первой покупки, +1м = через 1 месяц и т.д.</p>
            </div>
            
            {/* B2B vs B2C Breakdown */}
            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-4">💼 B2B vs B2C</h3>
              
              {cohortData.customer_type_breakdown && (
                <div className="space-y-4">
                  {/* B2B - Clickable */}
                  <div 
                    onClick={() => setSelectedClientType(prev => prev === 'B2B' ? null : 'B2B')}
                    className={`rounded-lg p-4 shadow-sm cursor-pointer transition-all ${
                      selectedClientType === 'B2B' ? 'bg-indigo-50 ring-2 ring-indigo-300' : 
                      selectedClientType === 'B2C' ? 'bg-white opacity-40' : 'bg-white hover:bg-indigo-50'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-lg">🏢</span>
                      <span className="font-semibold text-gray-900">B2B</span>
                      {selectedClientType === 'B2B' && <span className="text-xs text-indigo-500 ml-auto">Фильтр активен</span>}
                    </div>
                    <p className="text-xl font-bold text-indigo-600">
                      {formatCurrency(cohortData.customer_type_breakdown.B2B?.revenue || 0)}
                    </p>
                    <p className="text-sm text-gray-500">
                      {cohortData.customer_type_breakdown.B2B?.orders || 0} заказов
                    </p>
                  </div>
                  
                  {/* B2C - Clickable */}
                  <div 
                    onClick={() => setSelectedClientType(prev => prev === 'B2C' ? null : 'B2C')}
                    className={`rounded-lg p-4 shadow-sm cursor-pointer transition-all ${
                      selectedClientType === 'B2C' ? 'bg-purple-50 ring-2 ring-purple-300' : 
                      selectedClientType === 'B2B' ? 'bg-white opacity-40' : 'bg-white hover:bg-purple-50'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-lg">👤</span>
                      <span className="font-semibold text-gray-900">B2C</span>
                      {selectedClientType === 'B2C' && <span className="text-xs text-purple-500 ml-auto">Фильтр активен</span>}
                    </div>
                    <p className="text-xl font-bold text-purple-600">
                      {formatCurrency(cohortData.customer_type_breakdown.B2C?.revenue || 0)}
                    </p>
                    <p className="text-sm text-gray-500">
                      {cohortData.customer_type_breakdown.B2C?.orders || 0} заказов
                    </p>
                  </div>
                  
                  {/* Ratio Bar */}
                  <div className="mt-4">
                    <div className="h-3 rounded-full overflow-hidden flex bg-gray-200">
                      {(() => {
                        const b2b = cohortData.customer_type_breakdown.B2B?.revenue || 0;
                        const b2c = cohortData.customer_type_breakdown.B2C?.revenue || 0;
                        const total = b2b + b2c;
                        const b2bPct = total > 0 ? (b2b / total * 100) : 50;
                        return (
                          <>
                            <div className="bg-indigo-500 h-full" style={{ width: `${b2bPct}%` }}></div>
                            <div className="bg-purple-500 h-full" style={{ width: `${100 - b2bPct}%` }}></div>
                          </>
                        );
                      })()}
                    </div>
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>B2B</span>
                      <span>B2C</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Period Comparison Section */}
      {periodComparison && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">📊 Сравнение периодов</h2>
              <p className="text-sm text-gray-500">
                {periodComparison.current_period.start} — {periodComparison.current_period.end} vs {periodComparison.previous_period.start} — {periodComparison.previous_period.end}
              </p>
            </div>
          </div>
          
          {/* Metrics Comparison Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            {[
              { key: 'revenue', label: 'Выручка', format: formatCurrency },
              { key: 'orders', label: 'Заказов', format: v => v },
              { key: 'unique_clients', label: 'Клиентов', format: v => v },
              { key: 'avg_order', label: 'Ср. чек', format: formatCurrency },
              { key: 'items_sold', label: 'Продано шт', format: v => v }
            ].map(({ key, label, format }) => (
              <div key={key} className="bg-gray-50 rounded-xl p-4">
                <p className="text-xs text-gray-500 mb-1">{label}</p>
                <p className="text-lg font-bold text-gray-900">{format(periodComparison.current_period.metrics[key])}</p>
                <div className={`flex items-center text-xs mt-1 ${periodComparison.changes[key] >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {periodComparison.changes[key] >= 0 ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
                  {periodComparison.changes[key] > 0 ? '+' : ''}{periodComparison.changes[key]}%
                </div>
                <p className="text-xs text-gray-400 mt-1">Было: {format(periodComparison.previous_period.metrics[key])}</p>
              </div>
            ))}
          </div>
          
          {/* Daily Comparison Chart */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">Динамика по дням</h3>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
                <Line 
                  data={periodComparison.current_period.daily} 
                  type="monotone" 
                  dataKey="revenue" 
                  stroke="#6366f1" 
                  strokeWidth={2} 
                  name="Текущий" 
                  dot={false}
                />
                <Line 
                  data={periodComparison.previous_period.daily} 
                  type="monotone" 
                  dataKey="revenue" 
                  stroke="#9ca3af" 
                  strokeWidth={2} 
                  strokeDasharray="5 5"
                  name="Прошлый" 
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* ABC Analysis Section */}
      {abcData && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">📈 ABC-анализ (Парето)</h2>
              <p className="text-sm text-gray-500">Правило 80/20: какие товары/клиенты приносят основную выручку</p>
            </div>
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setAbcTab('products')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  abcTab === 'products' ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Товары
              </button>
              <button
                onClick={() => setAbcTab('clients')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  abcTab === 'clients' ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Клиенты
              </button>
            </div>
          </div>
          
          {/* Summary Cards - CLICKABLE */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            {['A', 'B', 'C'].map(cat => {
              const data = abcTab === 'products' ? abcData.products.summary[cat] : abcData.clients.summary[cat];
              const isSelected = selectedAbcCategory === cat;
              const isDimmed = selectedAbcCategory && !isSelected;
              const colors = {
                A: { bg: 'bg-green-50', text: 'text-green-600', border: 'border-green-200', ring: 'ring-green-300' },
                B: { bg: 'bg-yellow-50', text: 'text-yellow-600', border: 'border-yellow-200', ring: 'ring-yellow-300' },
                C: { bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200', ring: 'ring-red-300' }
              };
              return (
                <div 
                  key={cat} 
                  onClick={() => setSelectedAbcCategory(prev => prev === cat ? null : cat)}
                  className={`${colors[cat].bg} ${colors[cat].border} border rounded-xl p-4 cursor-pointer transition-all ${
                    isSelected ? `ring-2 ${colors[cat].ring} scale-105` : isDimmed ? 'opacity-40' : 'hover:scale-102'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-2xl font-bold ${colors[cat].text}`}>Категория {cat}</span>
                    <span className={`text-xs px-2 py-1 rounded-full ${colors[cat].bg} ${colors[cat].text} font-medium`}>
                      {cat === 'A' ? '0-80%' : cat === 'B' ? '80-95%' : '95-100%'}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <p className="text-gray-500">Позиций</p>
                      <p className="font-semibold">{data.count} ({data.pct_items}%)</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Выручка</p>
                      <p className="font-semibold">{data.pct_revenue}%</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Pareto Chart - Bars highlight by selected ABC */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">Кривая Парето {selectedAbcCategory && <span className="text-xs text-indigo-500 ml-2">— категория {selectedAbcCategory}</span>}</h3>
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={(abcTab === 'products' ? abcData.products.items : abcData.clients.items).slice(0, 20)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="rank" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 11 }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} tickFormatter={v => `${v}%`} domain={[0, 100]} />
                  <Tooltip 
                    formatter={(value, name) => [
                      name === 'cumulative_pct' ? `${value}%` : formatCurrency(value),
                      name === 'cumulative_pct' ? 'Накоплено' : 'Выручка'
                    ]}
                    labelFormatter={(rank) => {
                      const items = abcTab === 'products' ? abcData.products.items : abcData.clients.items;
                      const item = items.find(i => i.rank === rank);
                      return item ? `${item.name} (${item.category})` : `#${rank}`;
                    }}
                  />
                  <Legend />
                  <Bar 
                    yAxisId="left" dataKey="revenue" name="Выручка" radius={[4, 4, 0, 0]}
                    onClick={(data) => {
                      if (data && data.category) {
                        setSelectedAbcCategory(prev => prev === data.category ? null : data.category);
                      }
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    {(abcTab === 'products' ? abcData.products.items : abcData.clients.items).slice(0, 20).map((entry, index) => {
                      const isSelected = selectedAbcCategory === entry.category;
                      const isDimmed = selectedAbcCategory && !isSelected;
                      return (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={entry.category === 'A' ? '#22c55e' : entry.category === 'B' ? '#f59e0b' : '#ef4444'} 
                          opacity={isDimmed ? 0.2 : 1}
                        />
                      );
                    })}
                  </Bar>
                  <Line yAxisId="right" type="monotone" dataKey="cumulative_pct" stroke="#6366f1" strokeWidth={2} name="Накопленный %" dot={false} />
                  <ReferenceLine yAxisId="right" y={80} stroke="#ef4444" strokeDasharray="5 5" label={{ value: '80%', position: 'right', fontSize: 10 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            
            {/* ABC Table - Filtered by selected category */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-700">
                  {selectedAbcCategory ? `Категория ${selectedAbcCategory}` : 'Детализация по категориям'}
                </h3>
                {selectedAbcCategory && (
                  <button 
                    onClick={() => setSelectedAbcCategory(null)} 
                    className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                  >
                    <X className="w-3 h-3" /> Показать все
                  </button>
                )}
              </div>
              <div className="overflow-y-auto max-h-[300px]">
                <table className="min-w-full text-sm">
                  <thead className="sticky top-0 bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">#</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Название</th>
                      <th className="px-3 py-2 text-right font-medium text-gray-600">Выручка</th>
                      <th className="px-3 py-2 text-right font-medium text-gray-600">%</th>
                      <th className="px-3 py-2 text-center font-medium text-gray-600">ABC</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(abcTab === 'products' ? abcData.products.items : abcData.clients.items)
                      .filter(item => !selectedAbcCategory || item.category === selectedAbcCategory)
                      .slice(0, 15).map((item, idx) => (
                      <tr 
                        key={item.id} 
                        className={`cursor-pointer transition-colors ${
                          idx % 2 === 0 ? 'bg-white hover:bg-indigo-50' : 'bg-gray-50 hover:bg-indigo-50'
                        }`}
                        onClick={() => {
                          if (abcTab === 'products') {
                            setSelectedProductId(prev => prev === item.id ? null : item.id);
                            setSelectedProductName(item.name);
                          } else {
                            setSelectedClientId(prev => prev === item.id ? null : item.id);
                            setSelectedClientName(item.name);
                          }
                        }}
                      >
                        <td className="px-3 py-2 text-gray-500">{item.rank}</td>
                        <td className="px-3 py-2 font-medium text-gray-900 truncate max-w-[150px]" title={item.name}>
                          {item.name}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-900">{formatCurrency(item.revenue)}</td>
                        <td className="px-3 py-2 text-right text-gray-500">{item.cumulative_pct}%</td>
                        <td className="px-3 py-2 text-center">
                          <span className={`inline-block px-2 py-1 text-xs font-bold rounded ${
                            item.category === 'A' ? 'bg-green-100 text-green-700' :
                            item.category === 'B' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {item.category}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ML Forecasting Section */}
      {mlForecast && (
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl shadow-sm border border-indigo-100 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">🤖 ML-прогнозирование</h2>
              <p className="text-sm text-gray-500">Продвинутый прогноз с доверительными интервалами</p>
            </div>
            <div className="flex bg-white/80 rounded-lg p-1 shadow-sm">
              <button
                onClick={() => setMlTab('revenue')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  mlTab === 'revenue' ? 'bg-indigo-500 text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Выручка
              </button>
              <button
                onClick={() => setMlTab('orders')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  mlTab === 'orders' ? 'bg-indigo-500 text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Заказы
              </button>
              <button
                onClick={() => setMlTab('products')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  mlTab === 'products' ? 'bg-indigo-500 text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Товары
              </button>
            </div>
          </div>
          
          {/* ML data-quality warnings */}
          {mlForecast.warnings && mlForecast.warnings.length > 0 && (
            <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="ml-warnings">
              <p className="text-xs font-medium text-amber-800 mb-1">⚠️ Качество модели:</p>
              <ul className="text-xs text-amber-700 space-y-0.5 list-disc list-inside">
                {mlForecast.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}
          
          {/* Model Quality Badge */}
          {mlForecast[mlTab === 'revenue' ? 'revenue' : mlTab === 'orders' ? 'orders' : 'items_sold']?.model_quality && (
            <div className="flex items-center gap-4 mb-4">
              <div className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${
                mlForecast[mlTab === 'revenue' ? 'revenue' : mlTab === 'orders' ? 'orders' : 'items_sold'].model_quality.accuracy_rating === 'high' 
                  ? 'bg-green-100 text-green-700' 
                  : mlForecast[mlTab === 'revenue' ? 'revenue' : mlTab === 'orders' ? 'orders' : 'items_sold'].model_quality.accuracy_rating === 'medium'
                  ? 'bg-yellow-100 text-yellow-700'
                  : 'bg-red-100 text-red-700'
              }`}>
                📊 Точность модели: {mlForecast[mlTab === 'revenue' ? 'revenue' : mlTab === 'orders' ? 'orders' : 'items_sold'].model_quality.accuracy_rating === 'high' ? 'Высокая' : mlForecast[mlTab === 'revenue' ? 'revenue' : mlTab === 'orders' ? 'orders' : 'items_sold'].model_quality.accuracy_rating === 'medium' ? 'Средняя' : 'Низкая'}
              </div>
              <span className="text-sm text-gray-600">
                MAPE: {mlForecast[mlTab === 'revenue' ? 'revenue' : mlTab === 'orders' ? 'orders' : 'items_sold'].model_quality.mape}%
              </span>
              <span className="text-sm text-gray-600">
                Тренд: {mlForecast[mlTab === 'revenue' ? 'revenue' : mlTab === 'orders' ? 'orders' : 'items_sold'].trend?.direction === 'growing' ? '📈 Рост' : mlForecast[mlTab === 'revenue' ? 'revenue' : mlTab === 'orders' ? 'orders' : 'items_sold'].trend?.direction === 'declining' ? '📉 Снижение' : '➡️ Стабильно'}
              </span>
            </div>
          )}
          
          {mlTab !== 'products' && mlForecast[mlTab === 'revenue' ? 'revenue' : 'orders'] && (
            <div>
              {/* Forecast Chart */}
              <ResponsiveContainer width="100%" height={350}>
                <AreaChart data={[
                  ...(mlForecast[mlTab === 'revenue' ? 'revenue' : 'orders'].historical || []).map(h => ({
                    ...h,
                    isHistorical: true
                  })),
                  ...(mlForecast[mlTab === 'revenue' ? 'revenue' : 'orders'].forecast || []).map(f => ({
                    date: f.date,
                    forecast: f.forecast,
                    lower_bound: f.lower_bound,
                    upper_bound: f.upper_bound,
                    isForecast: true
                  }))
                ]}>
                  <defs>
                    <linearGradient id="colorActual" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorForecast" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={v => mlTab === 'revenue' ? `${(v/1000).toFixed(0)}k` : v} />
                  <Tooltip 
                    formatter={(value, name) => [
                      mlTab === 'revenue' ? formatCurrency(value) : value,
                      name === 'actual' ? 'Факт' : name === 'forecast' ? 'Прогноз' : name === 'fitted' ? 'Модель' : name
                    ]}
                  />
                  <Legend />
                  <Area type="monotone" dataKey="actual" stroke="#6366f1" fill="url(#colorActual)" strokeWidth={2} name="Факт" />
                  <Line type="monotone" dataKey="fitted" stroke="#f59e0b" strokeWidth={2} strokeDasharray="5 5" name="Модель" dot={false} />
                  <Area type="monotone" dataKey="forecast" stroke="#22c55e" fill="url(#colorForecast)" strokeWidth={2} name="Прогноз" />
                  <Area type="monotone" dataKey="upper_bound" stroke="#22c55e" strokeOpacity={0.3} fill="none" strokeDasharray="3 3" name="Верхняя граница" />
                  <Area type="monotone" dataKey="lower_bound" stroke="#22c55e" strokeOpacity={0.3} fill="none" strokeDasharray="3 3" name="Нижняя граница" />
                </AreaChart>
              </ResponsiveContainer>
              
              {/* Forecast Summary Table */}
              {mlForecast[mlTab === 'revenue' ? 'revenue' : 'orders'].forecast && mlForecast[mlTab === 'revenue' ? 'revenue' : 'orders'].forecast.length > 0 && (
                <div className="mt-4 bg-white/80 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">📅 Прогноз на ближайшие месяцы</h3>
                  <div className="grid grid-cols-3 gap-4">
                    {mlForecast[mlTab === 'revenue' ? 'revenue' : 'orders'].forecast.map((f, idx) => (
                      <div key={f.date} className="bg-white rounded-lg p-3 shadow-sm border border-gray-100">
                        <p className="text-xs text-gray-500 mb-1">{f.date}</p>
                        <p className="text-lg font-bold text-gray-900">
                          {mlTab === 'revenue' ? formatCurrency(f.forecast) : f.forecast}
                        </p>
                        <p className="text-xs text-gray-400">
                          {mlTab === 'revenue' ? formatCurrency(f.lower_bound) : f.lower_bound} — {mlTab === 'revenue' ? formatCurrency(f.upper_bound) : f.upper_bound}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Product Forecasts */}
          {mlTab === 'products' && productForecast && productForecast.products && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Прогноз спроса для топ-{productForecast.metadata?.top_n || 10} товаров
              </p>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-white/80">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Товар</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-600">Всего продано</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-600">Выручка</th>
                      <th className="px-4 py-3 text-center font-medium text-gray-600">Тренд</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-600">Прогноз (шт/мес)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {productForecast.products.slice(0, 10).map((product, idx) => (
                      <tr key={product.product_id} className={idx % 2 === 0 ? 'bg-white/60' : 'bg-white/30'}>
                        <td className="px-4 py-3 font-medium text-gray-900 max-w-[200px] truncate" title={product.product_name}>
                          {product.product_name}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-700">{product.total_quantity} шт</td>
                        <td className="px-4 py-3 text-right text-gray-700">{formatCurrency(product.total_revenue)}</td>
                        <td className="px-4 py-3 text-center">
                          {product.quantity_forecast?.trend?.direction === 'growing' ? (
                            <span className="inline-flex items-center text-green-600"><TrendingUp className="w-4 h-4 mr-1" />Рост</span>
                          ) : product.quantity_forecast?.trend?.direction === 'declining' ? (
                            <span className="inline-flex items-center text-red-600"><TrendingDown className="w-4 h-4 mr-1" />Спад</span>
                          ) : (
                            <span className="text-gray-500">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {product.quantity_forecast?.forecast?.[0] ? (
                            <span className="font-medium text-indigo-600">
                              ~{product.quantity_forecast.forecast[0].forecast} шт
                            </span>
                          ) : (
                            <span className="text-gray-400">Мало данных</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {/* Seasonality info */}
          {mlForecast[mlTab === 'revenue' ? 'revenue' : mlTab === 'orders' ? 'orders' : 'items_sold']?.seasonality?.has_seasonality && (
            <div className="mt-4 p-4 bg-white/60 rounded-lg">
              <h3 className="text-sm font-medium text-gray-700 mb-2">📆 Обнаружена сезонность</h3>
              <p className="text-xs text-gray-500">
                Модель учитывает недельные и месячные паттерны в данных для более точного прогноза
              </p>
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      {selectedClient && (
        <ClientDetailModal clientId={selectedClient} onClose={() => setSelectedClient(null)} />
      )}
      {selectedProduct && (
        <ProductDetailModal productId={selectedProduct} onClose={() => setSelectedProduct(null)} />
      )}
    </div>
  );
}
